/**
 * AudioManager - Handles game audio
 * Note: Web Audio API requires user interaction before audio can play
 */

export interface SoundConfig {
  volume?: number;
  pitch?: number;
  loop?: boolean;
}

class AudioManagerClass {
  private audioContext: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;

  private sounds: Map<string, AudioBuffer> = new Map();
  private currentMusic: AudioBufferSourceNode | null = null;
  private musicId: string | null = null;

  private masterVolume: number = 1;
  private musicVolume: number = 0.5;
  private sfxVolume: number = 0.8;
  private muted: boolean = false;

  /** Initialize audio context (must be called after user interaction) */
  init(): void {
    if (this.audioContext) return;

    try {
      this.audioContext = new AudioContext();

      // Create gain nodes
      this.masterGain = this.audioContext.createGain();
      this.musicGain = this.audioContext.createGain();
      this.sfxGain = this.audioContext.createGain();

      // Connect: music/sfx -> master -> output
      this.musicGain.connect(this.masterGain);
      this.sfxGain.connect(this.masterGain);
      this.masterGain.connect(this.audioContext.destination);

      // Set initial volumes
      this.masterGain.gain.value = this.masterVolume;
      this.musicGain.gain.value = this.musicVolume;
      this.sfxGain.gain.value = this.sfxVolume;

      console.log('AudioManager initialized');
    } catch (e) {
      console.error('Failed to initialize audio:', e);
    }
  }

  /** Check if initialized */
  isInitialized(): boolean {
    return this.audioContext !== null;
  }

  /** Load a sound from URL */
  async loadSound(id: string, url: string): Promise<boolean> {
    if (!this.audioContext) {
      console.warn('AudioContext not initialized');
      return false;
    }

    try {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      this.sounds.set(id, audioBuffer);
      return true;
    } catch (e) {
      console.error(`Failed to load sound ${id}:`, e);
      return false;
    }
  }

  /** Add a pre-decoded audio buffer */
  addSound(id: string, buffer: AudioBuffer): void {
    this.sounds.set(id, buffer);
  }

  /** Play a sound effect */
  playSound(id: string, config: SoundConfig = {}): void {
    if (!this.audioContext || !this.sfxGain || this.muted) return;

    const buffer = this.sounds.get(id);
    if (!buffer) {
      // console.warn(`Sound not found: ${id}`);
      return;
    }

    const source = this.audioContext.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = config.pitch ?? 1;
    source.loop = config.loop ?? false;

    // Create gain for this sound
    const gainNode = this.audioContext.createGain();
    gainNode.gain.value = config.volume ?? 1;

    source.connect(gainNode);
    gainNode.connect(this.sfxGain);

    source.start();
  }

  /** Play music (loops by default) */
  playMusic(id: string, loop: boolean = true): void {
    if (!this.audioContext || !this.musicGain) return;

    // Stop current music
    this.stopMusic();

    const buffer = this.sounds.get(id);
    if (!buffer) {
      console.warn(`Music not found: ${id}`);
      return;
    }

    this.currentMusic = this.audioContext.createBufferSource();
    this.currentMusic.buffer = buffer;
    this.currentMusic.loop = loop;
    this.currentMusic.connect(this.musicGain);
    this.currentMusic.start();
    this.musicId = id;
  }

  /** Stop current music */
  stopMusic(): void {
    if (this.currentMusic) {
      try {
        this.currentMusic.stop();
      } catch {
        // Already stopped
      }
      this.currentMusic = null;
      this.musicId = null;
    }
  }

  /** Set master volume (0-1) */
  setMasterVolume(volume: number): void {
    this.masterVolume = Math.max(0, Math.min(1, volume));
    if (this.masterGain) {
      this.masterGain.gain.value = this.muted ? 0 : this.masterVolume;
    }
  }

  /** Set music volume (0-1) */
  setMusicVolume(volume: number): void {
    this.musicVolume = Math.max(0, Math.min(1, volume));
    if (this.musicGain) {
      this.musicGain.gain.value = this.musicVolume;
    }
  }

  /** Set SFX volume (0-1) */
  setSfxVolume(volume: number): void {
    this.sfxVolume = Math.max(0, Math.min(1, volume));
    if (this.sfxGain) {
      this.sfxGain.gain.value = this.sfxVolume;
    }
  }

  getMasterVolume(): number {
    return this.masterVolume;
  }

  getMusicVolume(): number {
    return this.musicVolume;
  }

  getSfxVolume(): number {
    return this.sfxVolume;
  }

  /** Mute all audio */
  mute(): void {
    this.muted = true;
    if (this.masterGain) {
      this.masterGain.gain.value = 0;
    }
  }

  /** Unmute audio */
  unmute(): void {
    this.muted = false;
    if (this.masterGain) {
      this.masterGain.gain.value = this.masterVolume;
    }
  }

  /** Toggle mute */
  toggleMute(): void {
    if (this.muted) {
      this.unmute();
    } else {
      this.mute();
    }
  }

  isMuted(): boolean {
    return this.muted;
  }

  /** Get current music ID */
  getCurrentMusicId(): string | null {
    return this.musicId;
  }

  /** Clean up */
  destroy(): void {
    this.stopMusic();
    this.sounds.clear();
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }
}

// Singleton instance
export const AudioManager = new AudioManagerClass();
